import {
  Keyword,
  KeywordsWithCategoryData,
  toKeyword,
  toKeywordsWithCategoryData,
  toKeywordPostReqDTO,
  toKeywordPutReqDTO,
} from "../model/keyword";
import {
  KeywordGetResDTO,
  KeywordsWithCategoryGetResDTO,
} from "./keyword-dto.js";
import { getApiClient } from "../../../shared/api/client.api";
import { CommonResponseDto } from "../../../shared/api/common-response.dto";

/**
 * Keyword API 클라이언트
 * Keyword 관련 API 호출을 담당
 */
export const manageKeywordApi = (() => {
  const apiClient = getApiClient();

  const getKeywordList = async (): Promise<Keyword[]> => {
    try {
      const response =
        await apiClient.get<CommonResponseDto<KeywordGetResDTO[]>>("/keywords");

      if (!response.data.success || !response.data.data) {
        throw new Error("Keyword list data not found");
      }

      return response.data.data.map((keyword: KeywordGetResDTO) =>
        toKeyword(keyword),
      );
    } catch (error) {
      console.error("Failed to fetch keyword list:", error);
      throw error;
    }
  };

  const getKeyword = async (keywordId: string): Promise<Keyword> => {
    try {
      const response = await apiClient.get<CommonResponseDto<KeywordGetResDTO>>(
        `/keywords/${keywordId}`,
      );

      if (!response.data.success || !response.data.data) {
        throw new Error("Keyword data not found");
      }

      return toKeyword(response.data.data);
    } catch (error) {
      console.error("Failed to fetch keyword:", error);
      throw error;
    }
  };

  const getKeywordsWithCategory = async (): Promise<
    KeywordsWithCategoryData[]
  > => {
    try {
      const response = await apiClient.get<
        CommonResponseDto<KeywordsWithCategoryGetResDTO[]>
      >("/keywords/perCategory");

      if (!response.data.success || !response.data.data) {
        throw new Error("Keywords with category data not found");
      }

      return response.data.data.map((data: KeywordsWithCategoryGetResDTO) =>
        toKeywordsWithCategoryData(data),
      );
    } catch (error) {
      console.error("Failed to fetch keywords with category:", error);
      throw error;
    }
  };

  const addKeyword = async (
    keyword: Omit<
      Keyword,
      "id" | "createdAt" | "updatedAt" | "categoryNameKo" | "categoryNameEn"
    >,
  ): Promise<KeywordGetResDTO> => {
    try {
      const payload = toKeywordPostReqDTO(keyword);
      const response = await apiClient.post<
        CommonResponseDto<KeywordGetResDTO>
      >("/keywords", payload);

      if (!response.data.success || !response.data.data) {
        throw new Error("Failed to add keyword");
      }

      return response.data.data;
    } catch (error) {
      console.error("Failed to add keyword:", error);
      throw error;
    }
  };

  const updateKeyword = async (
    keywordId: string,
    keyword: Partial<Keyword>,
  ): Promise<KeywordGetResDTO> => {
    try {
      const payload = toKeywordPutReqDTO({ ...keyword, id: keywordId });
      const response = await apiClient.put<CommonResponseDto<KeywordGetResDTO>>(
        `/keywords/${keywordId}`,
        payload,
      );

      if (!response.data.success || !response.data.data) {
        throw new Error("Failed to update keyword");
      }

      return response.data.data;
    } catch (error) {
      console.error("Failed to update keyword:", error);
      throw error;
    }
  };

  const deleteKeyword = async (keywordId: string): Promise<void> => {
    try {
      await apiClient.delete(`/keywords/${keywordId}`);
    } catch (error) {
      console.error("Failed to delete keyword:", error);
      throw error;
    }
  };

  return {
    getKeywordList,
    getKeyword,
    getKeywordsWithCategory,
    addKeyword,
    updateKeyword,
    deleteKeyword,
  };
})();
